import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, User, Calendar as CalendarIcon, IdCard } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import Transcript from "../components/post-call/Transcript";
import Summary from "../components/post-call/Summary";
import Soap from "../components/post-call/Soap";
import Billing from "../components/post-call/Billing";
import Reccomendations from "../components/post-call/Reccomendations";
import Clusters from "../components/post-call/Clusters";
import DoctorNotes from "../components/post-call/DoctorNotes";
import EmotionalConnect from "../components/post-call/EmotionalConnect";
import CallFeedback from "../components/post-call/PostCallFeedback";
import { fetchCallHistory, fetchDoctorsFromHistory } from "../api/callHistory";
import { fetchSummaryByAppointment } from "../api/summary";
import { fetchTranscriptByAppointment } from "../api/transcript";
import { fetchSoapNotes } from "../api/soap";
import { fetchRecommendationByAppointment } from "../api/recommendations";
import { fetchBillingByAppointment } from "../api/billingcodes";
import { fetchClustersByAppointment } from "../api/clusters";
import {
  fetchEmotionalEmpathy,
  fetchLongitudinalSentiment,
  fetchSentimentAnalysis,
} from "../api/emotionalConnect";
import { fetchAppointmentDetails } from "../redux/appointment-actions";
import { useAnyPermission, usePermission } from "../hooks/use-permission";

const POST_CALL_WINDOW_MS = 10 * 60 * 1000;
const EMPTY_FINAL_FETCH_STATUSES = {
  summary: "idle",
  transcript: "idle",
  soap: "idle",
  recommendations: "idle",
  billing: "idle",
  clusters: "idle",
  emotionalEmpathy: "idle",
  sentimentAnalysis: "idle",
  longitudinalSentiment: "idle",
};

const getSummaryText = (response) =>
  response?.data?.data?.full_summary_text || response?.data?.full_summary_text ||
  response?.full_summary_text || response?.summary || "";

const getTranscriptText = (response) =>
  response?.data?.data?.full_conversation ||
  response?.data?.full_conversation ||
  response?.full_conversation ||
  "";

const getSoapText = (response) =>
  response?.data?.data?.data?.soap_notes ?? response?.data?.data?.soap_notes ??
  response?.data?.soap_notes ?? response?.soap_notes ?? "";

const getRecommendationsText = (response) =>
  response?.data?.data?.recommendations ?? response?.data?.recommendations ??
  response?.recommendations ?? "";

const hasBillingDocument = (response) => {
  const billingData = response?.data?.data || response?.data || response;
  const billingText = billingData?.billing_codes || billingData?.engine_v1_gpt || "";
  const candidates = billingData?.engine_v2_search?.cpt_candidates;
  return (typeof billingText === "string" && billingText.trim().length > 0) ||
    (Array.isArray(candidates) && candidates.some((candidate) => candidate?.code));
};

const hasClustersDocument = (response) => {
  const clusters = response?.data;
  return Boolean(
    (Array.isArray(clusters?.clustered_output) && clusters.clustered_output.length) ||
    (Array.isArray(clusters?.medications_discussed) && clusters.medications_discussed.length) ||
    (clusters?.conditions_mentioned?.count > 0 &&
      Object.keys(clusters.conditions_mentioned.conditions || {}).length)
  );
};

const getEmotionalConnectData = (response) =>
  response?.data?.data?.data ??
  response?.data?.data ??
  response?.data ??
  response ??
  {};

const hasEmotionalEmpathyDocument = (response) => {
  const data = getEmotionalConnectData(response);
  return Boolean(
    data?.summary ||
    data?.communication_style?.statement ||
    data?.response_to_information?.statement ||
    data?.attitude_toward_treatment?.statement ||
    data?.recommended_approach?.statement ||
    data?.motivations_and_drivers?.length ||
    data?.stress_triggers_or_barriers?.length ||
    data?.personal_interests?.length ||
    data?.confidence?.score
  );
};

const hasSentimentAnalysisDocument = (response) => {
  const data = getEmotionalConnectData(response);
  return Boolean(
    data?.overall_summary?.statement ||
    data?.sentiment_breakdown?.positive ||
    data?.sentiment_breakdown?.negative ||
    data?.self_perception_of_progress?.statement
  );
};

const hasLongitudinalSentimentDocument = (response) => {
  const data = getEmotionalConnectData(response);
  return Boolean(
    data?.overall_summary?.statement ||
    data?.sentiment_trend?.positive ||
    data?.sentiment_trend?.negative ||
    data?.self_perception_of_progress?.statement
  );
};

const getEmpathyRequestData = (appointment) => ({
  first_name: appointment?.first_name || appointment?.firstName || "",
  last_name: appointment?.last_name || appointment?.lastName || "",
  email: appointment?.email || appointment?.patient_email || "",
});

const formatCallDuration = (startTime, endTime) => {
  const start = new Date(startTime || 0).getTime();
  const end = new Date(endTime || 0).getTime();

  if (!start || !end || end < start) return "";

  const totalSeconds = Math.floor((end - start) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds} seconds`;
  if (seconds === 0) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  return `${minutes}m ${seconds}s`;
};

const PostCallDocumentation = ({ onSave }) => {
  const [docTab, setDocTab] = useState("summary");
  const { callId } = useParams();
  const [prevPage, setPrevPage] = useState(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const appointments = useSelector((state) => state.appointments.appointments);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const searchParams = useSearchParams()[0];
  const username = searchParams.get("username");
  const clinicName = useSelector((state) => state.me?.me?.clinicName || "");
  const currentUserEmail = useSelector((state) => state.me?.me?.email || "");
  const [resolvedUsername, setResolvedUsername] = useState(username || "");
  const [callDuration, setCallDuration] = useState("");
  const [callEndTimeState, setCallEndTimeState] = useState({ callId: null, value: null });
  const callEndTime = callEndTimeState.callId === callId ? callEndTimeState.value : null;
  const [expiryState, setExpiryState] = useState({ key: "", expired: false });
  const [finalFetchState, setFinalFetchState] = useState({
    key: "",
    statuses: EMPTY_FINAL_FETCH_STATUSES,
  });
  const autoFinalFetchRef = useRef(new Set());
  const finalFetchInFlightRef = useRef(new Map());
  const previousDocTabRef = useRef(docTab);
  const [emotionalConnectDocument, setEmotionalConnectDocument] =
    useState("emotionalEmpathy");
  const endTimestamp = callEndTime ? new Date(callEndTime).getTime() : NaN;
  const deadlineKey =
    callId &&
    resolvedUsername &&
    Number.isFinite(endTimestamp)
      ? `${callId}:${resolvedUsername}:${endTimestamp}`
      : "";
  const postCallWindowExpired =
    Boolean(deadlineKey && expiryState.key === deadlineKey && expiryState.expired);
  const finalFetchStatuses =
    finalFetchState.key === deadlineKey
      ? finalFetchState.statuses
      : EMPTY_FINAL_FETCH_STATUSES;

  const updateFinalFetchStatuses = useCallback(
    (documentKey, status) => {
      setFinalFetchState((current) => {
        const statuses =
          current.key === deadlineKey
            ? current.statuses
            : EMPTY_FINAL_FETCH_STATUSES;
        return {
          key: deadlineKey,
          statuses: { ...statuses, [documentKey]: status },
        };
      });
    },
    [deadlineKey]
  );

  const canViewPostCall = usePermission("post_call.view_all", "read");
  const canViewPatientInfo = usePermission("patients.info", "read");
  const canOpenPatientRecord = useAnyPermission([
    { required: "patients.info", level: "read" },
    { required: "patients.clinical_summary", level: "read" },
    { required: "patients.upcoming_appointment", level: "read" },
  ]);
  const canViewSoap = usePermission("post_call.edit_soap_notes", "read");
  const canEditSoap = usePermission("post_call.edit_soap_notes", "write");
  const canViewBilling = usePermission("post_call.edit_billing_codes", "read");
  const canEditBilling = usePermission("post_call.edit_billing_codes", "write");
  const canViewDoctorNotes = useAnyPermission([
    { required: "post_call.add_doctor_notes", level: "read" },
    { required: "post_call.edit_doctor_notes", level: "read" },
  ]);
  const canCreateDoctorNotes = usePermission("post_call.add_doctor_notes", "write");
  const canEditDoctorNotes = usePermission("post_call.edit_doctor_notes", "write");
  const canManageFeedback = useAnyPermission([
    { required: "post_call.add_feedback", level: "write" },
    { required: "post_call.edit_feedback", level: "write" },
  ]);

  useEffect(() => {
    document.title = "Practice Intelligence - Seismic Connect";
  }, []);

  useEffect(() => {
    setResolvedUsername(username || "");
  }, [username]);

  useEffect(() => {
    if (resolvedUsername) {
      dispatch(fetchAppointmentDetails(resolvedUsername));
    }
  }, [dispatch, resolvedUsername]);

  useEffect(() => {
    let isActive = true;
    let timeoutId;

    const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

    const resolveAndPollCallHistory = async () => {
      const candidateEmails = new Set(
        [
          username,
          currentUserEmail,
          selectedAppointment?.doctor_email,
          selectedAppointment?.doctorEmail,
          selectedAppointment?.userID,
        ]
          .map(normalizeEmail)
          .filter(Boolean)
      );

      if (clinicName) {
        try {
          const clinicUsers = await fetchDoctorsFromHistory(clinicName);
          (Array.isArray(clinicUsers) ? clinicUsers : []).forEach((user) => {
            const email = normalizeEmail(user?.doctor_email || user?.email || user?.id);
            if (email) candidateEmails.add(email);
          });
        } catch (error) {
          console.warn("Could not fetch clinic users; continuing with known emails.", error);
        }
      }

      const poll = async () => {
        if (!isActive) return;

        if (candidateEmails.size === 0) {
          timeoutId = window.setTimeout(poll, 3000);
          return;
        }

        try {
          const history = await fetchCallHistory(Array.from(candidateEmails));
          const matchingEntry = (Array.isArray(history) ? history : [])
            .filter((entry) => String(entry?.appointmentID) === String(callId))
            .sort(
              (a, b) =>
                new Date(b?.endTime || b?.startTime || 0) -
                new Date(a?.endTime || a?.startTime || 0)
            )[0];

          if (!isActive) return;

          const matchingUser = normalizeEmail(matchingEntry?.userID);
          if (matchingUser) setResolvedUsername(matchingUser);

          const validEndTime = matchingEntry?.endTime &&
            Number.isFinite(new Date(matchingEntry.endTime).getTime())
            ? matchingEntry.endTime
            : null;

          setCallDuration(formatCallDuration(matchingEntry?.startTime, validEndTime));

          if (validEndTime) {
            setCallEndTimeState({ callId, value: validEndTime });
            return;
          }
        } catch {
          // Keep polling until actual call end time is available.
        }

        if (isActive) timeoutId = window.setTimeout(poll, 3000);
      };

      await poll();
    };

    void resolveAndPollCallHistory();

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    callId,
    clinicName,
    currentUserEmail,
    selectedAppointment?.doctor_email,
    selectedAppointment?.doctorEmail,
    selectedAppointment?.userID,
    username,
  ]);

  const runFinalFetchForDocument = useCallback(async (documentKey) => {
    if (!callId || !resolvedUsername || !deadlineKey) return;

    const configs = {
      summary: {
        queryKey: ["summary", callId, resolvedUsername],
        request: () => fetchSummaryByAppointment(`${resolvedUsername}_${callId}_summary`, resolvedUsername),
        hasData: (response) => Boolean(String(getSummaryText(response) || "").trim()),
      },
      transcript: {
        queryKey: ["transcript", callId, resolvedUsername],
        request: () => fetchTranscriptByAppointment(`${resolvedUsername}_${callId}_transcription`, resolvedUsername),
        hasData: (response) => Boolean(String(getTranscriptText(response) || "").trim()),
      },
      soap: {
        queryKey: ["soap-notes", callId, resolvedUsername],
        request: () => fetchSoapNotes(`${resolvedUsername}_${callId}_soap`, resolvedUsername),
        hasData: (response) => Boolean(String(getSoapText(response) || "").trim()),
      },
      recommendations: {
        queryKey: ["recommendations", callId, resolvedUsername],
        request: () => fetchRecommendationByAppointment(`${resolvedUsername}_${callId}_recommendations`, resolvedUsername),
        hasData: (response) => Boolean(String(getRecommendationsText(response) || "").trim()),
      },
      billing: {
        queryKey: ["billing-codes", callId, resolvedUsername],
        request: () => fetchBillingByAppointment(`${resolvedUsername}_${callId}_billing`, resolvedUsername),
        hasData: hasBillingDocument,
      },
      clusters: {
        queryKey: ["clusters", callId, resolvedUsername],
        request: () => fetchClustersByAppointment(`${resolvedUsername}_${callId}_clusters`, resolvedUsername),
        hasData: hasClustersDocument,
      },
      emotionalEmpathy: {
        queryKey: ["emotionalEmpathy", callId, resolvedUsername],
        request: () =>
          fetchEmotionalEmpathy(
            getEmpathyRequestData(selectedAppointment),
            resolvedUsername
          ),
        hasData: hasEmotionalEmpathyDocument,
      },
      sentimentAnalysis: {
        queryKey: ["sentiment_analysis", callId, resolvedUsername],
        request: () =>
          fetchSentimentAnalysis(
            `${resolvedUsername}_${callId}_sentiment`,
            resolvedUsername
          ),
        hasData: hasSentimentAnalysisDocument,
      },
      longitudinalSentiment: {
        queryKey: ["longitudinalSentiment", callId, resolvedUsername],
        request: () =>
          fetchLongitudinalSentiment(
            `${resolvedUsername}_${callId}_longitudinal_sentiment`,
            resolvedUsername
          ),
        hasData: hasLongitudinalSentimentDocument,
      },
    };

    const config = configs[documentKey];
    if (!config) return;

    const requestKey = `${deadlineKey}:${documentKey}`;
    const inFlightRequest = finalFetchInFlightRef.current.get(requestKey);
    if (inFlightRequest) {
      await inFlightRequest;
      return;
    }

    const requestPromise = (async () => {
      updateFinalFetchStatuses(documentKey, "fetching");
      try {
        await queryClient.cancelQueries({ queryKey: config.queryKey, exact: true });
        const response = await config.request();
        if (config.hasData(response)) {
          queryClient.setQueryData(config.queryKey, response);
        }
      } catch (error) {
        console.error(`Failed to fetch final post-call ${documentKey} data.`, error);
      } finally {
        updateFinalFetchStatuses(documentKey, "done");
      }
    })();
    finalFetchInFlightRef.current.set(requestKey, requestPromise);

    try {
      await requestPromise;
    } finally {
      if (finalFetchInFlightRef.current.get(requestKey) === requestPromise) {
        finalFetchInFlightRef.current.delete(requestKey);
      }
    }
  }, [
    callId,
    resolvedUsername,
    deadlineKey,
    queryClient,
    selectedAppointment,
    updateFinalFetchStatuses,
  ]);

  const runFinalFetchForCurrentTab = useCallback((tab) => {
    const documentByTab = {
      summary: "summary",
      transcript: "transcript",
      soap: "soap",
      recommendations: "recommendations",
      billing: "billing",
      clusters: "clusters",
      emotionalConnect: emotionalConnectDocument,
    };
    const documentKey = documentByTab[tab];
    if (documentKey) void runFinalFetchForDocument(documentKey);
  }, [emotionalConnectDocument, runFinalFetchForDocument]);

  const handleEmotionalDocumentSelect = useCallback(
    (documentKey) => {
      setEmotionalConnectDocument(documentKey);
      if (postCallWindowExpired) {
        void runFinalFetchForDocument(documentKey);
      }
    },
    [postCallWindowExpired, runFinalFetchForDocument]
  );

  useEffect(() => {
    if (!callId || !resolvedUsername || !callEndTime) return undefined;

    const endTimestamp = new Date(callEndTime).getTime();
    if (!Number.isFinite(endTimestamp)) return undefined;

    const deadline = endTimestamp + POST_CALL_WINDOW_MS;
    const executeDeadlineFetches = () => {
      setExpiryState({ key: deadlineKey, expired: true });
      if (autoFinalFetchRef.current.has(deadlineKey)) return;

      autoFinalFetchRef.current.add(deadlineKey);
      // Make one final attempt for each core post-call document at the 10-minute deadline.
      [
        "summary",
        "transcript",
        "soap",
        "recommendations",
        "billing",
        "clusters",
        "emotionalEmpathy",
        "sentimentAnalysis",
        "longitudinalSentiment",
      ].forEach((key) => {
        void runFinalFetchForDocument(key);
      });
    };

    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) {
      executeDeadlineFetches();
      return undefined;
    }

    const timeoutId = window.setTimeout(executeDeadlineFetches, remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [callId, resolvedUsername, callEndTime, deadlineKey, runFinalFetchForDocument]);

  useEffect(() => {
    const previousTab = previousDocTabRef.current;
    previousDocTabRef.current = docTab;

    if (previousTab !== docTab && postCallWindowExpired) {
      // One direct fetch whenever the doctor opens/revisits a supported document after the deadline.
      runFinalFetchForCurrentTab(docTab);
    }
  }, [docTab, postCallWindowExpired, runFinalFetchForCurrentTab]);

  useEffect(() => {
    previousDocTabRef.current = "summary";
    setEmotionalConnectDocument("emotionalEmpathy");
    setDocTab("summary");
  }, [callId]);

  useEffect(() => {
    if (appointments?.length > 0) {
      const found = appointments.find((app) => String(app.id) === String(callId));
      setSelectedAppointment(found || null);
    }
  }, [appointments, callId]);

  useEffect(() => {
    document.title = "PostCallDocumentation - Seismic Connect";
    const state = window.history.state;
    if (state?.from) setPrevPage(state.from);
  }, []);

  const handleback = () => {
    if (window.history.length > 1) window.history.back();
    else navigate("/appointments");
  };

  const mrn = selectedAppointment
    ? selectedAppointment.mrn ??
    selectedAppointment.MRN ??
    selectedAppointment.patient_mrn ??
    null
    : null;

  const rawDob = selectedAppointment
    ? selectedAppointment.dob ??
    selectedAppointment.date_of_birth ??
    selectedAppointment.birthDate ??
    null
    : null;

  const dob = rawDob
    ? new Date(rawDob).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    })
    : null;

  const firstName = selectedAppointment
    ? selectedAppointment.first_name ??
    selectedAppointment.firstName ??
    selectedAppointment.full_name?.split(" ")[0] ??
    null
    : null;

  const lastName = selectedAppointment
    ? selectedAppointment.last_name ??
    selectedAppointment.lastName ??
    selectedAppointment.full_name?.split(" ").slice(1).join(" ") ??
    null
    : null;

  const documentationTabs = [
    canViewPostCall && { id: "summary", label: "Summary" },
    canViewPostCall && { id: "transcript", label: "Transcript" },
    canViewSoap && { id: "soap", label: "SOAP" },
    canViewPostCall && { id: "recommendations", label: "Recommendations" },
    canViewBilling && { id: "billing", label: "Billing" },
    canViewPostCall && { id: "clusters", label: "Clusters" },
    canViewDoctorNotes && { id: "doctorNotes", label: "Doctor Notes" },
    canViewPostCall && { id: "emotionalConnect", label: "Emotional Connect" },
  ].filter(Boolean);

  useEffect(() => {
    if (documentationTabs.length === 0) {
      return;
    }

    const hasCurrentTab = documentationTabs.some((tab) => tab.id === docTab);
    if (!hasCurrentTab) {
      setDocTab(documentationTabs[0].id);
    }
  }, [docTab, documentationTabs]);

  return (
    <>
      <div className="flex justify-between items-start mb-4">
        {prevPage !== "video-call" && (
          <button
            onClick={handleback}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-700 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back
          </button>
        )}

        {canManageFeedback && (
          <div className="ml-auto">
            <CallFeedback username={resolvedUsername} appointmentId={callId} />
          </div>
        )}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Practice Intelligence</CardTitle>
        </CardHeader>

        {selectedAppointment && canViewPatientInfo && (
          <div className="bg-white border border-gray-300 rounded-xl shadow p-6 mb-6 mx-6 md:mx-auto md:max-w-5xl">
            <h2 className="text-2xl font-semibold mb-4 text-gray-700">Patient Info</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-gray-800">
              <p className="flex items-center gap-2">
                <IdCard className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">MRN:</span>
                <span className="font-medium text-gray-900">{mrn ?? "—"}</span>
              </p>

              <p className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">DOB:</span>
                <span className="font-medium text-gray-900">{dob ?? "—"}</span>
              </p>

              <p className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">First Name:</span>
                <span className="font-medium text-gray-900">{firstName ?? "—"}</span>
              </p>

              <p className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">Last Name:</span>
                <span className="font-medium text-gray-900">{lastName ?? "—"}</span>
              </p>
            </div>

            {canOpenPatientRecord ? (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => navigate(`/patients?mrn=${mrn}`)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow"
                >
                  View patient
                </button>
              </div>
            ) : null}
          </div>
        )}

        <CardContent>
          <div className="mb-6 flex flex-wrap justify-center gap-2">
            {documentationTabs.map((tab) => (
              <button
                key={tab.id}
                className={`px-4 py-2 rounded-md font-medium transition ${docTab === tab.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white text-neutral-800 border border-neutral-300 hover:bg-neutral-50"
                  } transition`}
                onClick={() => setDocTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {docTab === "summary" && (
            <Summary
              username={resolvedUsername}
              appointmentId={callId}
              patientId={
                selectedAppointment?.patient_id ||
                selectedAppointment?.patient_Id ||
                selectedAppointment?.patientId ||
                selectedAppointment?.mrn ||
                selectedAppointment?.patient_mrn
              }
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              summaryFinalFetchStatus={finalFetchStatuses.summary}
            />
          )}

          {docTab === "transcript" && (
            <Transcript
              username={resolvedUsername}
              appointmentId={callId}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatus={finalFetchStatuses.transcript}
            />
          )}

          {docTab === "soap" && (
            <Soap
              username={resolvedUsername}
              appointmentId={callId}
              appointment={selectedAppointment}
              duration={callDuration}
              canEdit={canEditSoap}
              canPostToAthena={canEditSoap}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatus={finalFetchStatuses.soap}
            />
          )}

          {docTab === "recommendations" && (
            <Reccomendations
              username={resolvedUsername}
              appointmentId={callId}
              appointment={selectedAppointment}
              activeDocument={emotionalConnectDocument}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatus={finalFetchStatuses.recommendations}
            />
          )}

          {docTab === "billing" && (
            <Billing
              username={resolvedUsername}
              appointmentId={callId}
              canEdit={canEditBilling}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatus={finalFetchStatuses.billing}
            />
          )}

          {docTab === "clusters" && (
            <Clusters
              username={resolvedUsername}
              appointmentId={callId}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatus={finalFetchStatuses.clusters}
            />
          )}

          {docTab === "doctorNotes" && (
            <DoctorNotes
              username={resolvedUsername}
              appointmentId={callId}
              canCreate={canCreateDoctorNotes}
              canEditExisting={canEditDoctorNotes}
            />
          )}

          {docTab === "emotionalConnect" && selectedAppointment && (
            <EmotionalConnect
              username={resolvedUsername}
              appointmentId={callId}
              appointment={selectedAppointment}
              callEndTime={callEndTime}
              postCallWindowExpired={postCallWindowExpired}
              finalFetchStatuses={{
                emotionalEmpathy: finalFetchStatuses.emotionalEmpathy,
                sentimentAnalysis: finalFetchStatuses.sentimentAnalysis,
                longitudinalSentiment: finalFetchStatuses.longitudinalSentiment,
              }}
              onDocumentSelect={handleEmotionalDocumentSelect}
            />
          )}

        </CardContent>
      </Card>
    </>
  );
};

export default PostCallDocumentation;
